import { useEffect, useRef, useState } from 'react';
import { Box, Button, IconButton, Popover, Typography } from '@mui/material';
import { MoreVert, Delete, Edit, Lock } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import styles from '../FolderMoreOptions/FolderMoreOptions.module.scss';

type FolderMoreOptionsProps = {
  className?: string;
  onRename?: () => void;
  onDelete?: () => void;
  onManageAccess?: () => void; // ✅ NEW
  onDismissByClickAway?: () => void;
};

export function FolderMoreOptions({
  className,
  onRename,
  onDelete,
  onManageAccess,
  onDismissByClickAway,
}: FolderMoreOptionsProps) {
  const { t } = useTranslation('settings');
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const popoverPaperRef = useRef<HTMLDivElement | null>(null);
  const open = Boolean(anchorEl);

  const handleOpen = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    setAnchorEl(e.currentTarget);
  };

  const handleClose = () => setAnchorEl(null);

  useEffect(() => {
    if (!open) return;

    const handleOutsidePointerDown = (event: MouseEvent) => {
      const targetNode = event.target as Node | null;

      const clickedInsideAnchor = !!anchorEl && !!targetNode && anchorEl.contains(targetNode);
      const clickedInsidePopover =
        !!popoverPaperRef.current && !!targetNode && popoverPaperRef.current.contains(targetNode);

      if (!clickedInsideAnchor && !clickedInsidePopover) {
        event.preventDefault();
        event.stopPropagation();
        onDismissByClickAway?.();
        setAnchorEl(null);
      }
    };

    document.addEventListener('mousedown', handleOutsidePointerDown, true);

    return () => {
      document.removeEventListener('mousedown', handleOutsidePointerDown, true);
    };
  }, [open, anchorEl, onDismissByClickAway]);

  return (
    <>
      <IconButton className={className} onClick={handleOpen}>
        <MoreVert
          sx={(theme) => ({
            color: theme.palette.mode === 'dark' ? theme.palette.text.primary : theme.palette.text.secondary,
          })}
        />
      </IconButton>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={(_, reason) => {
          if (reason === 'backdropClick') {
            onDismissByClickAway?.();
          }
          handleClose();
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        slotProps={{
          paper: {
            ref: popoverPaperRef,
            sx: {
              backgroundColor: 'background.paper',
              boxShadow: '0px 4px 10px rgba(0, 0, 0, 0.15)',
            },
          },
        }}
      >
        <Box
          className={styles.popoverBox}
          onClick={(e) => {
            e.stopPropagation();
            handleClose();
          }}
        >
          {/* ✅ Manage Access (Admins / Staff only, via prop) */}
          {onManageAccess && (
            <Button fullWidth onClick={onManageAccess}>
              <Lock sx={{ mr: 1 }} />
              <Typography variant='body5'>{t('documentsFolderMoreOptions.manageAccess')}</Typography>
            </Button>
          )}

          {onRename && (
            <Button fullWidth onClick={onRename}>
              <Edit sx={{ mr: 1 }} />
              <Typography variant='body5'>{t('documentsFolderMoreOptions.rename')}</Typography>
            </Button>
          )}

          {onDelete && (
            <Button fullWidth onClick={onDelete}>
              <Delete sx={{ mr: 1 }} color='error' />
              <Typography variant='body5'>{t('documentsFolderMoreOptions.delete')}</Typography>
            </Button>
          )}
        </Box>
      </Popover>
    </>
  );
}

export default FolderMoreOptions;
